import { Button } from "../Button";
import { Dropdown } from "../Dropdown";
import { StatusBadge } from "../StatusBadge";
import { StatusPill } from "../StatusPill";
import { useTenantCustomDomain } from "../../hooks/useTenantCustomDomain";
import { useSessionTenantEdgeUrlOptions } from "../../hooks/useSessionTenantOrigin";
import { spaceEdgeOrigin } from "../../../shared/edgeUrl";
import { useState } from "react";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { queries } from "../../../shared/queries";
import { useCustomDomainsEnabled } from "../../hooks/useCustomDomainsEnabled";
import { countsTowardDomainLimit } from "../../../shared/customDomains";
import { sharedMutators } from "../../../shared/mutators";
import { MAX_CUSTOM_DOMAINS_PER_TENANT } from "../../../shared/stackLimits";
import { useSpaces } from "../../hooks/useSpaces";
import { navigate, routes, useCurrentUrl, useMakeUrl } from "../../lib/urls";
import { ConfirmModal } from "../ConfirmModal";
import { Toggle } from "../Toggle";
import { ButtonLink } from "../Button/ButtonLink";
import { EmptyState } from "../EmptyState";
import { Icon } from "../Icon";
import { ListTable } from "../ListTable";
import { IdentityCell } from "../Settings/IdentityCell";
import {
  Container,
  Header,
  HoverActions,
  ScrollArea,
} from "../Settings/shared";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { CustomDomainModal, type DomainChange } from "./CustomDomainModal";
import { DomainStatusPill } from "./DomainStatusPill";

export function CustomDomainSettings() {
  return useCustomDomainsEnabled() ? <DomainList /> : null;
}

function DomainList() {
  const z = useZero();
  const canManage = useCustomDomainsEnabled();
  const [confirmation, setConfirmation] = useState<{
    label: string;
    description: string;
    run: () => void;
  }>();
  const [domains, domainsResult] = useQuery(queries.customDomainSetups());
  const tenantDefault = useTenantCustomDomain();
  const edgeOptions = useSessionTenantEdgeUrlOptions();
  const [spaces] = useSpaces({ settingsView: true });
  const { domain: hostname } = useCurrentUrl(routes.settings.customDomains);
  const setup = domains.find((domain) => domain.hostname === hostname);
  const makeUrl = useMakeUrl();
  const open = (domain: string) =>
    navigate(routes.settings.customDomains, { domain }, { replace: true });
  function change(domain: (typeof domains)[number], args: DomainChange) {
    const run = () => {
      if (!canManage) return;
      z.mutate(sharedMutators.updateCustomDomain({ id: domain.id, ...args }));
    };
    if (domain.enabled && args.enabled === false)
      setConfirmation({
        label: "Disable domain",
        description: `Existing workflow URLs using “${domain.hostname}” may stop working immediately.`,
        run,
      });
    else run();
  }
  function remove(domain: (typeof domains)[number]) {
    setConfirmation({
      label: "Delete domain",
      description: domain.enabled
        ? `Workflow URLs using “${domain.hostname}” will stop working immediately. This can’t be undone.`
        : `“${domain.hostname}” will be removed. This can’t be undone.`,
      run: () => {
        z.mutate(sharedMutators.removeCustomDomain({ id: domain.id }));
        open("");
      },
    });
  }
  return (
    <Container>
      <Header style={{ paddingRight: 12 }}>
        <Text variant="text-regular-md">Domains</Text>
        <div style={{ marginLeft: "auto" }}>
          {domains.filter(countsTowardDomainLimit).length >=
          MAX_CUSTOM_DOMAINS_PER_TENANT ? (
            <Tooltip
              content={`You’ve reached the limit of ${MAX_CUSTOM_DOMAINS_PER_TENANT} custom domains.`}
            >
              <Button disabled icon="plus" size="l" aria-label="Add domain" />
            </Tooltip>
          ) : (
            <ButtonLink
              to={makeUrl(routes.settings.customDomains, { domain: "new" })}
              icon="plus"
              size="l"
              aria-label="Add domain"
            />
          )}
        </div>
      </Header>
      <ScrollArea>
        <ListTable
          headers={{
            domain: "Domain",
            status: "Status",
            enabled: "Enabled",
            actions: "",
          }}
          items={[
            ...domains.map((domain) => {
              const space = spaces.find((space) => space.id === domain.spaceId);
              const toggleDisabled =
                !domain.enabled && domain.status !== "ready";
              const isDefault =
                domain.enabled &&
                domain.status === "ready" &&
                domain.domain ===
                  (domain.spaceId ? space?.customHostname : tenantDefault);
              const scope = domain.spaceId
                ? `in ${space?.name ?? "this space"}`
                : "across the tenant";
              const actions = [
                {
                  type: "button" as const,
                  left: <Icon name="star" size={16} />,
                  label: (
                    <Tooltip
                      content={
                        isDefault
                          ? `Workflow URLs ${scope} go back to the ${domain.spaceId ? "tenant" : "built-in"} default.`
                          : `Workflow URLs ${scope} use this domain.`
                      }
                      fill
                    >
                      {isDefault ? "Remove as default" : "Set as default"}
                    </Tooltip>
                  ),
                  disabled: domain.status !== "ready",
                  onClick: () =>
                    change(domain, { enabled: true, setAsDefault: !isDefault }),
                },
                {
                  type: "button" as const,
                  left: <Icon name="trash" size={16} />,
                  label: "Delete",
                  onClick: () => remove(domain),
                },
              ];
              return {
                key: domain.id,
                link: makeUrl(routes.settings.customDomains, {
                  domain: domain.hostname,
                }),
                domain: (
                  <IdentityCell
                    title={domain.hostname}
                    subtitle={
                      domain.spaceId
                        ? (space?.name ?? "Deleted space")
                        : "Entire tenant"
                    }
                    icon={<Icon name="globe" size={16} />}
                    badge={isDefault && <DefaultBadge scope={scope} />}
                  />
                ),
                status: <DomainStatusPill setup={domain} />,
                enabled: (
                  <HoverActions visible>
                    <Tooltip
                      content="Finish setup to enable this domain."
                      hidden={!toggleDisabled}
                    >
                      <Toggle
                        isOn={!!domain.enabled}
                        disabled={toggleDisabled}
                        onToggle={() =>
                          change(domain, { enabled: !domain.enabled })
                        }
                        ariaLabel={`Enable ${domain.hostname}`}
                      />
                    </Tooltip>
                  </HoverActions>
                ),
                actions: (hasMouse: boolean) => (
                  <HoverActions visible={hasMouse}>
                    <Dropdown
                      opener={
                        <Button
                          variant="secondary"
                          icon="more"
                          aria-label={`Actions for ${domain.hostname}`}
                        />
                      }
                      sections={[{ items: actions }]}
                    />
                  </HoverActions>
                ),
              };
            }),
            {
              key: "built-in",
              domain: (
                <IdentityCell
                  title={decodeURIComponent(
                    new URL(spaceEdgeOrigin("*", edgeOptions)).host
                  )}
                  subtitle="Entire tenant"
                  icon={<Icon name="globe" size={16} />}
                  badge={
                    !tenantDefault && <DefaultBadge scope="across the tenant" />
                  }
                />
              ),
              status: (
                <StatusPill
                  passive
                  color="gray"
                  label="Built in"
                  detail={
                    <Text variant="text-small-md" color="var(--tooltip-fg)">
                      Ready to use.
                    </Text>
                  }
                />
              ),
              enabled: null,
              actions: null,
            },
          ]}
        />
        {!domains.length && (
          <EmptyState
            iconName="globe"
            title="Use your own domain"
            description="Serve workflow pages and webhooks from a domain you own, like workflows.example.com."
            maxWidth={360}
            action={
              <ButtonLink
                to={makeUrl(routes.settings.customDomains, { domain: "new" })}
                icon="plus"
              >
                Add domain
              </ButtonLink>
            }
          />
        )}
      </ScrollArea>
      {hostname && (setup || domainsResult.type === "complete") && (
        <CustomDomainModal
          setup={setup}
          onClose={() => open("")}
          onCreated={open}
          onChange={change}
          onDelete={remove}
        />
      )}
      {confirmation && (
        <ConfirmModal
          open
          onClose={() => setConfirmation(undefined)}
          onConfirm={() => {
            confirmation.run();
            setConfirmation(undefined);
          }}
          title={confirmation.label}
          description={confirmation.description}
          confirmLabel={confirmation.label}
          destructive
        />
      )}
    </Container>
  );
}

function DefaultBadge({ scope }: { scope: string }) {
  return (
    <Tooltip content={`Workflow URLs ${scope} use this domain.`}>
      <StatusBadge color="green">Default</StatusBadge>
    </Tooltip>
  );
}
